/**
 * subdomainHelper.js — Subdomain Detection & URL Building
 * ─────────────────────────────────────────────────────
 * CHANGES:
 *  1. Consistent JSDoc on all exports
 *  2. Minor formatting cleanup
 *  3. No functional changes — this file was already solid
 */

/**
 * Get subdomain information from current URL.
 * @returns {{ isSubdomain: boolean, subdomain: string|null, isMainDomain: boolean, environment: string, fullHostname: string }}
 */
export const getSubdomainInfo = () => {
  const hostname = window.location.hostname;
  const parts = hostname.split('.');

  // Localhost without subdomain
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return {
      isSubdomain: false,
      subdomain: null,
      isMainDomain: true,
      environment: 'development',
      fullHostname: hostname,
    };
  }

  // Localhost with subdomain (e.g., company1.localhost)
  if (hostname.includes('.localhost')) {
    const subdomain = hostname.split('.localhost')[0];
    return {
      isSubdomain: true,
      subdomain,
      isMainDomain: false,
      environment: 'development',
      fullHostname: hostname,
    };
  }

  // Production: 3+ parts → could be subdomain.domain.com
  if (parts.length >= 3) {
    const firstPart = parts[0];

    if (firstPart === 'www') {
      return {
        isSubdomain: false,
        subdomain: null,
        isMainDomain: true,
        environment: 'production',
        fullHostname: hostname,
      };
    }

    return {
      isSubdomain: true,
      subdomain: firstPart,
      isMainDomain: false,
      environment: 'production',
      fullHostname: hostname,
    };
  }

  // 2 parts → main domain (e.g., yourdomain.com)
  return {
    isSubdomain: false,
    subdomain: null,
    isMainDomain: true,
    environment: 'production',
    fullHostname: hostname,
  };
};

/**
 * Get subdomain string for API calls.
 * @returns {string}
 */
export const getSubDomain = () => {
  const info = getSubdomainInfo();
  return info.subdomain || '';
};

/**
 * Build a full URL for a given subdomain.
 * @param {string} subdomain
 * @param {string} path - Default '/login'
 * @returns {string}
 */
export const buildSubdomainUrl = (subdomain, path = '/login') => {
  const protocol = window.location.protocol;
  const hostname = window.location.hostname;
  const port = window.location.port ? `:${window.location.port}` : '';

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `${protocol}//${subdomain}.localhost${port}${path}`;
  }

  const parts = hostname.split('.');
  let mainDomain;

  if (parts.length >= 3) {
    mainDomain = parts.slice(-2).join('.');
    // Handle country code TLDs (.co.uk, .com.au)
    if (parts[parts.length - 2].length === 2) {
      mainDomain = parts.slice(-3).join('.');
    }
  } else {
    mainDomain = hostname;
  }

  return `${protocol}//${subdomain}.${mainDomain}${port}${path}`;
};

/**
 * Check if running in development environment.
 * @returns {boolean}
 */
export const isDevelopment = () => {
  return getSubdomainInfo().environment === 'development';
};

/**
 * Get main domain without subdomain prefix.
 * @returns {string}
 */
export const getMainDomain = () => {
  const hostname = window.location.hostname;

  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.includes('.localhost')) {
    return 'localhost';
  }

  const parts = hostname.split('.');
  if (parts.length >= 3) {
    const mainDomain = parts.slice(-2).join('.');
    if (parts[parts.length - 2].length === 2) {
      return parts.slice(-3).join('.');
    }
    return mainDomain;
  }

  return hostname;
};

/**
 * Extract subdomain from various input formats.
 * Handles: "acme", "acme.yourdomain.com", "https://acme.yourdomain.com"
 * @param {string} input
 * @returns {string|null}
 */
export const extractSubdomain = (input) => {
  if (!input) return null;

  let domain = input.trim()
    .replace(/^https?:\/\//, '')
    .replace(/\/$/, '')
    .split('/')[0]
    .split(':')[0];

  if (!domain.includes('.')) {
    return domain.toLowerCase();
  }

  const parts = domain.split('.');
  return parts.length >= 2 ? parts[0].toLowerCase() : null;
};

/**
 * Validate subdomain format (alphanumeric with hyphens, 1-63 chars).
 * @param {string} subdomain
 * @returns {boolean}
 */
export const isValidSubdomain = (subdomain) => {
  if (!subdomain) return false;
  return /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/i.test(subdomain);
};

/**
 * Debug helper — logs subdomain info to console.
 */
export const logSubdomainInfo = () => {
  const info = getSubdomainInfo();
  console.log('=== Subdomain Information ===');
  console.log('Full Hostname:', info.fullHostname);
  console.log('Is Subdomain:', info.isSubdomain);
  console.log('Subdomain:', info.subdomain || 'None');
  console.log('Is Main Domain:', info.isMainDomain);
  console.log('Environment:', info.environment);
  console.log('Main Domain:', getMainDomain());
  console.log('===========================');
  return info;
};
